import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  EyeIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { formatStatus, getStatusBadgeClass, truncateText } from "../../utils/Utils";
import {
  Page,
  PageHeader,
  PageLoader,
  Panel,
  SearchInput,
  SectionHeading,
  StatTile,
  TableShell,
  Td,
  TdEmpty,
  Th,
  Tr,
} from "../../components/ui";

const AdminDashboard = () => {
  const [files, setFiles] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get(`/dashboard`);
        setDashboard(response.data);
        setFiles(response.data.files);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const filteredFiles = files.filter(
    (file) =>
      file.file_id
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Page>
      <PageHeader title="Dashboard" subtitle="Everything waiting on the team right now." />

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile
          label="Processed"
          value={loading ? "…" : dashboard?.completed ?? 0}
          tone="success"
          icon={<CheckCircleIcon className="h-6 w-6" />}
        />
        <StatTile
          label="Refunded"
          value={loading ? "…" : dashboard?.refunded ?? 0}
          tone="info"
          icon={<ArrowUturnLeftIcon className="h-6 w-6" />}
        />
        <StatTile
          label="Canceled"
          value={loading ? "…" : dashboard?.canceled ?? 0}
          tone="danger"
          icon={<XCircleIcon className="h-6 w-6" />}
        />
      </div>

      {loading ? (
        <PageLoader label="Loading the console…" />
      ) : (
        <>
          <SearchInput
            placeholder="Search by file id, file name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="grid gap-5 xl:grid-cols-2">
            <Panel>
              <SectionHeading title="Last Files" />
              <TableShell className="mt-4" maxHeight="max-h-[520px]">
                <thead>
                  <tr>
                    <Th>File ID</Th>
                    <Th>Username</Th>
                    <Th>File Name</Th>
                    <Th>Status</Th>
                    <Th align="center">View</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.length === 0 ? <TdEmpty colSpan={5}>No files found.</TdEmpty> : null}
                  {filteredFiles.map((file) => (
                    <Tr key={file.id}>
                      <Td className="font-mono text-xs">{file.file_id}</Td>
                      <Td className="font-medium text-gray-800 dark:text-slate-200">
                        {file.username}
                      </Td>
                      <Td>{truncateText(file.file_name, 16)}</Td>
                      <Td>
                        <span className={getStatusBadgeClass(file.status)}>
                          {formatStatus(file.status)}
                        </span>
                      </Td>
                      <Td className="text-center">
                        <Link
                          to={`/admin/view-file/${file.id}`}
                          title="View file"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </TableShell>
            </Panel>

            <Panel>
              <SectionHeading title="Last Tickets" />
              <TableShell className="mt-4" maxHeight="max-h-[520px]">
                <thead>
                  <tr>
                    <Th>Ticket ID</Th>
                    <Th>Username</Th>
                    <Th>Subject</Th>
                    <Th>Status</Th>
                    <Th align="center">View</Th>
                  </tr>
                </thead>
                <tbody>
                  {!dashboard?.tickets || dashboard.tickets.length === 0 ? (
                    <TdEmpty colSpan={5}>No tickets open.</TdEmpty>
                  ) : null}
                  {dashboard?.tickets?.map((ticket) => (
                    <Tr key={ticket.id}>
                      <Td className="font-mono text-xs">{ticket.ticket_id}</Td>
                      <Td className="font-medium text-gray-800 dark:text-slate-200">
                        {ticket.username}
                      </Td>
                      <Td>{truncateText(ticket.subject, 30)}</Td>
                      <Td>
                        <span className={getStatusBadgeClass(ticket.status)}>
                          {formatStatus(ticket.status)}
                        </span>
                      </Td>
                      <Td className="text-center">
                        <Link
                          to={`/admin/view-file/${ticket.file_id}`}
                          title="View ticket"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </TableShell>
            </Panel>
          </div>
        </>
      )}
    </Page>
  );
};

export default AdminDashboard;
